import { getSchemasForRoute } from "@/lib/schema";
import { getRouteMeta, siteName, siteUrl } from "@/router/routeMeta";

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => element?.setAttribute(key, value));
}

function upsertLink(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLLinkElement>(selector);
  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => element?.setAttribute(key, value));
}

function clearJsonLd() {
  document.head.querySelectorAll('script[data-seo-schema="true"]').forEach((node) => node.remove());
}

function appendJsonLd(schema: Record<string, unknown>) {
  const element = document.createElement("script");
  element.type = "application/ld+json";
  element.dataset.seoSchema = "true";
  element.text = JSON.stringify(schema);
  document.head.appendChild(element);
}

export default function SeoMeta() {
  const location = useLocation();

  useEffect(() => {
    const meta = getRouteMeta(location.pathname);
    const title = meta.title.includes(siteName) ? meta.title : `${meta.title} | ${siteName}`;
    const canonicalPath = meta.canonicalPath ?? location.pathname;
    const canonicalUrl = `${siteUrl}${canonicalPath}`;

    document.title = title;
    upsertMeta('meta[name="description"]', { name: "description", content: meta.description });
    upsertMeta('meta[name="keywords"]', { name: "keywords", content: meta.keywords });
    upsertMeta('meta[name="robots"]', { name: "robots", content: meta.robots ?? "index,follow" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: meta.description });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: meta.ogType ?? "website" });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: meta.description });
    upsertLink('link[rel="canonical"]', { rel: "canonical", href: canonicalUrl });

    clearJsonLd();
    getSchemasForRoute(location.pathname).forEach((schema) => appendJsonLd(schema));
  }, [location.pathname]);

  return null;
}
